import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  Form,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import {
  FaSearch,
  FaMapMarkerAlt,
  FaFilm,
  FaChevronRight,
  FaTicketAlt,
  FaStar,
  FaUsers,
  FaPlay,
  FaCalendarAlt,
  FaFilter,
  FaBuilding,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AOS from "aos";
import ModernLoader from "../components/common/ModernLoader";
import TheaterListLayout from "../components/theaters/TheaterListLayout";
import api from "../services/api";
import showsAPI from "../services/api/showsAPI";

const Theaters = () => {
  const navigate = useNavigate();
  const [theaters, setTheaters] = useState([]);
  const [filteredTheaters, setFilteredTheaters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedTheater, setSelectedTheater] = useState(null);
  const [theaterMovies, setTheaterMovies] = useState([]);
  const [loadingMovies, setLoadingMovies] = useState(false);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    fetchTheaters();
    AOS.init({ duration: 800, once: true });
    
    // Load user's saved location
    const savedLocation = localStorage.getItem('userLocation');
    if (savedLocation) {
      try {
        const location = JSON.parse(savedLocation);
        setSelectedCity(location.city);
      } catch (err) {
        console.error('Error loading saved location:', err);
      }
    }

    // Listen for location changes
    const handleLocationChange = () => {
      const updated = localStorage.getItem('userLocation');
      if (updated) {
        try {
          const location = JSON.parse(updated);
          setSelectedCity(location.city);
        } catch (err) {
          console.error('Error updating location:', err);
        }
      }
    };

    const interval = setInterval(handleLocationChange, 500);
    window.addEventListener('storage', handleLocationChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleLocationChange);
    };
  }, []);

  useEffect(() => {
    filterTheaters();
  }, [theaters, searchTerm, selectedCity]);

  const fetchTheaters = async () => {
    try {
      setLoading(true);
      const response = await api.get("/theaters/with-shows");
      const theatersData = Array.isArray(response.data)
        ? response.data
        : response.data.theaters || [];

      setTheaters(theatersData);
      const uniqueCities = [
        ...new Set(
          theatersData
            .map((t) => (t.address?.city || t.city)?.toLowerCase())
            .filter(Boolean)
        ),
      ].map((city) => city.charAt(0).toUpperCase() + city.slice(1));
      setCities(uniqueCities);
    } catch (error) {
      console.error("Fetch theaters error:", error);
      toast.error("Failed to fetch theaters");
    } finally {
      setLoading(false);
    }
  };

  const filterTheaters = () => {
    let filtered = theaters.filter(
      (theater) => theater.status?.isActive !== false
    );

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (theater) =>
          theater.name?.toLowerCase().includes(search) ||
          theater.location?.toLowerCase().includes(search) ||
          theater.address?.city?.toLowerCase().includes(search)
      );
    }

    if (selectedCity) {
      filtered = filtered.filter(
        (theater) =>
          (theater.address?.city || theater.city)?.toLowerCase() ===
          selectedCity.toLowerCase()
      );
    }

    setFilteredTheaters(filtered);
  };

  const handleMovieClick = (movieId, theater) => {
    const city = theater.address?.city || theater.city || selectedCity;
    // Store the selected city in localStorage for consistency
    if (city) {
      localStorage.setItem('selectedTheaterCity', city);
    }
    navigate(
      `/movie/${movieId}/showtimes?city=${encodeURIComponent(city)}&theaterId=${
        theater._id
      }`
    );
  };

  if (loading) {
    return (
      <div
        className="min-vh-100 d-flex align-items-center justify-content-center"
        style={{ backgroundColor: "#0f1419" }}
      >
        <ModernLoader text="Loading Theaters" />
      </div>
    );
  }

  return (
    <div
      className="min-vh-100"
      style={{
        backgroundColor: "#1f2025",
        paddingTop: "120px",
        paddingBottom: "50px",
      }}
    >
      <Container fluid="xl">
        {/* Hero Section */}
        <div className="text-center mb-5">
          <h1 className="fw-bold text-white mb-3" style={{ fontSize: "2.5rem" }}>
            Premium Theaters
          </h1>
          <p className="text-light mb-4" style={{ fontSize: "1.1rem", opacity: 0.8 }}>
            Find and book tickets at the best theaters near you
          </p>
          
          <Row className="justify-content-center g-3">
            <Col xs={4} md={2}>
              <div className="text-center">
                <h4 className="text-white fw-bold mb-1">{filteredTheaters.length}</h4>
                <small className="text-light">Theaters</small>
              </div>
            </Col>
            <Col xs={4} md={2}>
              <div className="text-center">
                <h4 className="text-white fw-bold mb-1">{cities.length}</h4>
                <small className="text-light">Cities</small>
              </div>
            </Col>
            <Col xs={4} md={2}>
              <div className="text-center">
                <h4 className="text-white fw-bold mb-1">24/7</h4>
                <small className="text-light">Support</small>
              </div>
            </Col>
          </Row>
        </div>

        {/* Search & Filter Section */}
        {!selectedTheater && (
          <div className="mb-4">
            <Card style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
              <Card.Body className="p-4">
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label className="text-white mb-2">Search Theaters</Form.Label>
                    <InputGroup>
                      <InputGroup.Text style={{ backgroundColor: "rgba(255,255,255,0.1)", color: "white", border: "1px solid rgba(255,255,255,0.2)" }}>
                        <FaSearch />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Search by name or location..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ backgroundColor: "rgba(255,255,255,0.1)", color: "white", border: "1px solid rgba(255,255,255,0.2)" }}
                      />
                    </InputGroup>
                  </Col>
                  <Col md={4}>
                    <Form.Label className="text-white mb-2"><FaMapMarkerAlt className="me-2" />Select City</Form.Label>
                    <Form.Select
                      value={selectedCity}
                      onChange={(e) => setSelectedCity(e.target.value)}
                      style={{ backgroundColor: "rgba(255,255,255,0.1)", color: "white", border: "1px solid rgba(255,255,255,0.2)" }}
                    >
                      <option value="" style={{ backgroundColor: "#2a2d3a", color: "#fff" }}>All Cities</option>
                      {cities.map((city) => (
                        <option key={city} value={city} style={{ backgroundColor: "#2a2d3a", color: "#fff" }}>
                          {city}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={2} className="d-flex align-items-end">
                    <Button
                      variant="outline-light"
                      className="w-100"
                      onClick={() => {
                        setSearchTerm("");
                        setSelectedCity("");
                      }}
                    >
                      Clear
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </div>
        )}

        {/* Results Header with Location */}
        <div className="mb-4 p-3 rounded-3" style={{ backgroundColor: "rgba(220, 53, 69, 0.1)", border: "1px solid rgba(220, 53, 69, 0.3)" }}>
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h5 className="text-white mb-1 fw-bold">
                <FaMapMarkerAlt className="me-2 text-danger" />
                {filteredTheaters.length} Theater{filteredTheaters.length !== 1 ? "s" : ""} in {selectedCity || "All Cities"}
              </h5>
              <p className="text-light mb-0" style={{ fontSize: "0.875rem" }}>
                {selectedCity ? `Showing all available theaters in ${selectedCity}` : "Showing theaters from all cities"}
              </p>
            </div>
            {selectedCity && (
              <Button
                variant="outline-light"
                size="sm"
                onClick={() => setSelectedCity("")}
                className="ms-3"
              >
                View All Cities
              </Button>
            )}
          </div>
        </div>

        {/* Theater List Layout */}
        <div
          className="theater-list-wrapper"
          style={{
            background: "rgba(255,255,255,0.05)",
            borderRadius: "12px",
            border: "1px solid rgba(255,255,255,0.1)",
            overflow: "hidden",
          }}
        >
            {filteredTheaters.length > 0 ? (
              <TheaterListLayout
                theaters={filteredTheaters}
                onMovieClick={handleMovieClick}
              />
            ) : (
              <div
                className="text-center py-5"
                style={{
                  padding: "60px 20px",
                  background: "#1f2025",
                  borderRadius: "12px",
                }}
              >
                <div
                  className="mb-4 mx-auto p-4 rounded-circle"
                  style={{
                    backgroundColor: "#1f2025",
                    width: "80px",
                    height: "80px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <FaBuilding size={32} className="text-white" />
                </div>
                <h4
                  className="text-white mb-3 fw-bold"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "1.4rem",
                    letterSpacing: "-0.01em",
                  }}
                >
                  No Theaters Found
                </h4>
                <p
                  className="text-light mb-4"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "1rem",
                    lineHeight: "1.6",
                    fontWeight: "400",
                    opacity: 0.9,
                  }}
                >
                  We couldn't find any theaters matching your search criteria.
                  Try adjusting your filters.
                </p>
                <Button
                  variant="outline-light"
                  className="px-4 py-2 fw-semibold"
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedCity("");
                  }}
                >
                  Clear All Filters
                </Button>
              </div>
            )}
        </div>
      </Container>
    </div>
  );
};

export default Theaters;
